"use client"

import { useState, useEffect } from "react"
import { Plus, Pencil, Trash2, Building2, X, AlertCircle, Loader2 } from "lucide-react"
import { getSchool, addBranch, updateBranch, removeBranch } from "@/hooks/api/schoolApi"

interface Branch {
  id: string
  name: string
  address?: { street?: string; city?: string; state?: string }
  phone?: string
  principalName?: string
  classLevels?: string[]
  isActive?: boolean
}

const EMPTY: Omit<Branch, "id"> = {
  name: "",
  address: { street: "", city: "", state: "" },
  phone: "",
  principalName: "",
  classLevels: [],
  isActive: true,
}


function BranchModal({
  branch,
  onSave,
  onClose,
}: {
  branch: Partial<Branch> | null
  onSave: (data: Omit<Branch, "id">) => void
  onClose: () => void
}) {
  const [form, setForm] = useState<Omit<Branch, "id">>(branch ? { ...EMPTY, ...branch } : { ...EMPTY })

  const set = (key: keyof Omit<Branch, "id">, val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">{branch?.id ? "Edit Branch" : "Add Branch"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Branch Name *</label>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Victoria Island Campus"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">City</label>
              <input
                value={form.address?.city ?? ""}
                onChange={(e) => set("address", { ...form.address, city: e.target.value })}
                placeholder="Lagos"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">State</label>
              <input
                value={form.address?.state ?? ""}
                onChange={(e) => set("address", { ...form.address, state: e.target.value })}
                placeholder="Lagos"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Street Address</label>
            <input
              value={form.address?.street ?? ""}
              onChange={(e) => set("address", { ...form.address, street: e.target.value })}
              placeholder="123 Example Street"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Phone</label>
              <input
                value={form.phone ?? ""}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="0801234567"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Principal</label>
              <input
                value={form.principalName ?? ""}
                onChange={(e) => set("principalName", e.target.value)}
                placeholder="Mr. Ade"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 p-5 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            disabled={!form.name.trim()}
            onClick={() => onSave(form)}
            className="flex-1 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold"
          >
            Save Branch
          </button>
        </div>
      </div>
    </div>
  )
}

export function BranchesTab() {
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Branch | null | "new">(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getSchool()
      .then((s) => setBranches((s as any).branches ?? []))
      .catch(err => setError(err?.response?.data?.error?.message ?? "Could not load branches."))
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async (data: Omit<Branch, "id">) => {
    setSaving(true)
    try {
      if (editing === "new") {
        const result: any = await addBranch(data)
        const newBranches = result?.school?.branches ?? result?.branches
        if (newBranches) {
          setBranches(newBranches)
        } else {
          setBranches((prev) => [...prev, { ...data, id: `b${Date.now()}` }])
        }
      } else if (editing) {
        const result: any = await updateBranch(editing.id, data)
        const newBranches = result?.school?.branches ?? result?.branches
        if (newBranches) {
          setBranches(newBranches)
        } else {
          setBranches((prev) => prev.map((b) => (b.id === editing.id ? { ...editing, ...data } : b)))
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? "Save failed.")
    } finally {
      setSaving(false)
      setEditing(null)
    }
  }

  const handleRemove = async (id: string) => {
    setDeleting(id)
    try {
      await removeBranch(id)
      setBranches((prev) => prev.filter((b) => b.id !== id))
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? "Remove failed.")
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading branches…</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-slate-900">School Branches</h3>
          <p className="text-sm text-slate-500 mt-0.5">{branches.length} branch{branches.length !== 1 ? "es" : ""} configured</p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-xl"
        >
          <Plus size={14} /> Add Branch
        </button>
      </div>

      {branches.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl py-12 text-center">
          <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No branches yet. Add your first branch to get started.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {branches.map((branch) => (
            <div key={branch.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-indigo-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm">{branch.name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[branch.address?.city, branch.address?.state].filter(Boolean).join(", ") || "No address"}
                  {branch.principalName ? ` · ${branch.principalName}` : ""}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setEditing(branch)}
                  className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"
                >
                  <Pencil size={14} />
                </button>
                <button
                  disabled={deleting === branch.id}
                  onClick={() => handleRemove(branch.id)}
                  className="p-2 rounded-xl hover:bg-red-50 text-red-400 disabled:opacity-50"
                >
                  {deleting === branch.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BranchModal
          branch={editing === "new" ? null : editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
